import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, defer, Observable, timer } from 'rxjs';
import { expand, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
  LiveConnectionState,
  LiveUpdateService,
} from '@core/services/live-update.service';
import { OutlineBadgeType } from '@app/@shared/outline-badge/outline-badge.component';
import { NumberTranslatePipe } from '@app/@shared/pipes/number-translate/number-translate.pipe';

interface LiveStatusDisplay {
  badgeType: OutlineBadgeType;
  badgeLabel: string;
  timeLabel: string;
}

/**
 * Shows the shared LiveUpdateService hub connection status (Live /
 * Reconnecting / Connection lost) plus an "Updating..." state a host page
 * can trigger while it's re-fetching data in response to a live signal.
 *
 * Usage: <app-live-status-indicator [lastUpdatedAt]="lastUpdatedAt" [isRefreshing]="isLiveRefreshing">
 * The host page is responsible for setting `lastUpdatedAt` after every
 * successful fetch (initial load and live-triggered refetch) and toggling
 * `isRefreshing` only around fetches triggered by a live signal.
 */
@Component({
  selector: 'app-live-status-indicator',
  templateUrl: './live-status-indicator.component.html',
  styleUrls: ['./live-status-indicator.component.scss'],
  standalone: false,
})
export class LiveStatusIndicatorComponent implements OnChanges {
  @Input() lastUpdatedAt: Date | number | null = null;
  @Input() isRefreshing = false;

  private readonly lastUpdatedAtSubject = new BehaviorSubject<
    Date | number | null
  >(null);
  private readonly isRefreshingSubject = new BehaviorSubject<boolean>(false);
  private destroyRef = inject(DestroyRef);

  readonly display$: Observable<LiveStatusDisplay>;

  constructor(
    private liveUpdateService: LiveUpdateService,
    private translateService: TranslateService,
    private numberTranslatePipe: NumberTranslatePipe,
  ) {
    // Ticks every second while under a minute old (so "3s ago" stays live),
    // then drops to once a minute once past that (the label only changes in
    // whole minutes from then on). Restarts from the 1s cadence whenever
    // lastUpdatedAt changes (a fresh fetch just happened).
    const tick$ = this.lastUpdatedAtSubject.pipe(
      switchMap((lastUpdatedAt) =>
        defer(() => timer(0)).pipe(
          expand(() => timer(this.nextTickDelay(lastUpdatedAt))),
          map(() => lastUpdatedAt),
        ),
      ),
    );

    this.display$ = combineLatest([
      this.liveUpdateService.connectionState$,
      tick$,
      this.isRefreshingSubject,
    ]).pipe(
      map(([connectionState, lastUpdatedAt, isRefreshing]) =>
        this.buildDisplay(connectionState, lastUpdatedAt, isRefreshing),
      ),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private nextTickDelay(lastUpdatedAt: Date | number | null): number {
    if (lastUpdatedAt == null) return 60000;
    const ts =
      typeof lastUpdatedAt === 'number'
        ? lastUpdatedAt
        : lastUpdatedAt.getTime();
    const elapsedMs = Date.now() - ts;
    return elapsedMs < 60000 ? 1000 : 60000;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('lastUpdatedAt' in changes) {
      this.lastUpdatedAtSubject.next(this.lastUpdatedAt);
    }
    if ('isRefreshing' in changes) {
      this.isRefreshingSubject.next(this.isRefreshing);
    }
  }

  private buildDisplay(
    connectionState: LiveConnectionState,
    lastUpdatedAt: Date | number | null,
    isRefreshing: boolean,
  ): LiveStatusDisplay {
    const timeLabel = this.formatTimeLabel(lastUpdatedAt, isRefreshing);

    if (isRefreshing) {
      return {
        badgeType: 'active',
        badgeLabel: this.translateService.instant('Updating...'),
        timeLabel,
      };
    }

    switch (connectionState) {
      case 'connected':
        return {
          badgeType: 'success',
          badgeLabel: this.translateService.instant('Live'),
          timeLabel,
        };
      case 'connecting':
      case 'reconnecting':
        return {
          badgeType: 'warning',
          badgeLabel: this.translateService.instant('Reconnecting...'),
          timeLabel,
        };
      case 'disconnected':
        return {
          badgeType: 'error',
          badgeLabel: this.translateService.instant('Connection lost'),
          timeLabel,
        };
    }
  }

  private formatTimeLabel(
    lastUpdatedAt: Date | number | null,
    isRefreshing: boolean,
  ): string {
    const prefixKey = isRefreshing
      ? 'Updated {{time}}'
      : 'Last updated {{time}}';
    const time =
      lastUpdatedAt == null
        ? this.translateService.instant('just now')
        : this.formatElapsed(
            typeof lastUpdatedAt === 'number'
              ? lastUpdatedAt
              : lastUpdatedAt.getTime(),
          );

    return this.translateService.instant(prefixKey, { time });
  }

  private formatElapsed(timestamp: number): string {
    const diffSeconds = Math.max(
      0,
      Math.floor((Date.now() - timestamp) / 1000),
    );

    if (diffSeconds < 5) {
      return this.translateService.instant('just now');
    }
    if (diffSeconds < 60) {
      return this.translateService.instant('{{n}}s ago', {
        n: this.numberTranslatePipe.transform(diffSeconds),
      });
    }
    if (diffSeconds < 3600) {
      return this.translateService.instant('{{n}}m ago', {
        n: this.numberTranslatePipe.transform(Math.floor(diffSeconds / 60)),
      });
    }

    const absoluteTime = new Date(timestamp).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    return this.numberTranslatePipe.transform(absoluteTime);
  }
}
